import { useEffect, useRef, useState } from 'react';

/**
 * Whole seconds since `active` last became true.
 *
 * A ticking number is the cheapest possible proof that a screen is not frozen.
 * A spinner cannot make that claim — a CSS animation keeps turning happily
 * while the tab's JavaScript is wedged, so users learn not to trust it. A
 * counter that advances is evidence the app is still running.
 *
 * `resetKey` restarts the count, so moving from one long step to the next reads
 * as a new wait rather than one that has already run for a minute.
 *
 * The interval is only created while active, and is torn down on the way out,
 * so an idle card costs nothing.
 */
const useElapsedSeconds = (active, resetKey) => {
  const [seconds, setSeconds] = useState(0);
  const startRef = useRef(null);

  useEffect(() => {
    if (!active) {
      startRef.current = null;
      setSeconds(0);
      return undefined;
    }
    startRef.current = Date.now();
    setSeconds(0);
    const id = setInterval(() => {
      if (startRef.current == null) return;
      setSeconds(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [active, resetKey]);

  return seconds;
};

export default useElapsedSeconds;
